import { FC, useState, useEffect } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Button,
  Icon,
  useColorModeValue,
  useToast,
  Spinner,
  Center,
  Badge,
  Divider,
  SimpleGrid,
  Avatar,
  Progress,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  StatArrow,
  IconButton,
  Tooltip,
  Input,
  InputGroup,
  InputLeftElement
} from '@chakra-ui/react';
import {
  MdPerson,
  MdCalendarToday,
  MdAccessTime,
  MdDelete,
  MdEdit,
  MdVisibility,
  MdSchool,
  MdSearch,
  MdAdd
} from 'react-icons/md';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';

const MotionBox = motion(Box);
import { ADMIN_ROUTES, formatDate } from '@bemap/utils';
import { Panel, PageHeading, AnimatedCard, containerVariants, cardVariants } from '@bemap/ui';
import {
  getAllStudents,
  unenrollStudent,
  CourseStudent
} from '@/services/university-service';

const StudentsListPage: FC = () => {
  const navigate = useNavigate();
  const { courseId } = useParams<{ courseId: string }>();
  const toast = useToast();
  const { t } = useTranslation();
  const [students, setStudents] = useState<CourseStudent[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedStudent, setSelectedStudent] = useState<CourseStudent | null>(null);
  const [unenrolling, setUnenrolling] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const { isOpen: isUnenrollModalOpen, onOpen: onUnenrollModalOpen, onClose: onUnenrollModalClose } = useDisclosure();

  const cardBg = useColorModeValue('white', 'gray.800');

  // Load all students data
  useEffect(() => {
    const loadStudents = async () => {
      try {
        setLoading(true);

        const response = await getAllStudents();

        if (response.statusCode === 200 && response.message) {
          setStudents(response.message as CourseStudent[]);
        } else {
          throw new Error(response.message || 'Failed to load students');
        }
      } catch (error: any) {
        console.error('Error loading students:', error);
        toast({
          title: t('university.students.error_loading_students'),
          description: t('general.error'),
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      } finally {
        setLoading(false);
      }
    };

    loadStudents();
  }, [courseId, toast, t]);

  const handleBack = () => {
    navigate(ADMIN_ROUTES.UNIVERSITY);
  };

  const handleUnenrollStudent = async () => {
    if (!selectedStudent) return;

    try {
      setUnenrolling(true);
      const response = await unenrollStudent(selectedStudent.courseId, selectedStudent.userId);

      if (response.statusCode === 200) {
        toast({
          title: t('university.students.student_unenrolled'),
          description: `${selectedStudent.user?.name || 'Student'} has been unenrolled from the course.`,
          status: 'success',
          duration: 3000,
          isClosable: true,
        });

        // Remove student from the list
        setStudents(prev => prev.filter(student => student.id !== selectedStudent.id));
        onUnenrollModalClose();
        setSelectedStudent(null);
      } else {
        throw new Error(response.message || 'Failed to unenroll student');
      }
    } catch (error: any) {
      console.error('Error unenrolling student:', error);
      toast({
        title: t('university.students.error_unenrolling_student'),
        description: error.message || 'Failed to unenroll student.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setUnenrolling(false);
    }
  };

  const getProgressColor = (progress: number) => {
    if (progress >= 80) return 'green';
    if (progress >= 60) return 'orange';
    if (progress >= 40) return 'yellow';
    return 'red';
  };

  // Filter students based on search term
  const filteredStudents = students.filter(student => {
    const searchLower = searchTerm.toLowerCase();
    return (
      student.user?.name?.toLowerCase().includes(searchLower) ||
      student.user?.email?.toLowerCase().includes(searchLower) ||
      student.course?.title?.toLowerCase().includes(searchLower)
    );
  });

  // Calculate statistics
  const totalStudents = students.length;
  const inProgressStudents = students.filter(s => s.status === 'in_progress').length;
  const completedStudents = students.filter(s => s.status === 'completed').length;
  const averageProgress = totalStudents > 0
    ? Math.round(students.reduce((sum, s) => sum + s.overallProgress, 0) / totalStudents)
    : 0;

  if (loading) {
    return (
      <Center h="50vh">
        <VStack spacing={4}>
          <Spinner size="xl" color="blue.500" />
          <Text>{t('university.students.loading_students')}</Text>
        </VStack>
      </Center>
    );
  }

  return (
    <VStack align="stretch" spacing={6}>
      {/* Header */}
      <PageHeading
        title={t('university.students.students_list')}
        actions={[
          {
            label: t('university.students.back_to_courses'),
            onClick: handleBack,
            icon: <Icon as={MdPerson} />,
            variant: 'outline'
          },
          {
            label: t('university.students.add_student'),
            onClick: () => navigate(`${ADMIN_ROUTES.UNIVERSITY_COURSES}/${courseId}/students/add`),
            icon: <Icon as={MdAdd} />
          }
        ]}
      />

      {/* Statistics */}
      <Panel heading={t('university.students.university_statistics')} padding={6}>
        <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={6}>
          <Stat>
            <StatLabel>{t('university.students.total_students')}</StatLabel>
            <StatNumber>{totalStudents}</StatNumber>
            <StatHelpText>
              <StatArrow type="increase" />
              {t('university.students.enrolled_across_all_courses')}
            </StatHelpText>
          </Stat>

          <Stat>
            <StatLabel>{t('university.students.in_progress')}</StatLabel>
            <StatNumber>{inProgressStudents}</StatNumber>
            <StatHelpText>
              <StatArrow type="increase" />
              {t('university.students.actively_learning')}
            </StatHelpText>
          </Stat>

          <Stat>
            <StatLabel>{t('university.students.completed')}</StatLabel>
            <StatNumber>{completedStudents}</StatNumber>
            <StatHelpText>
              <StatArrow type="increase" />
              {t('university.students.finished_courses')}
            </StatHelpText>
          </Stat>

          <Stat>
            <StatLabel>{t('university.students.average_progress')}</StatLabel>
            <StatNumber>{averageProgress}%</StatNumber>
            <StatHelpText>
              <StatArrow type={averageProgress > 50 ? "increase" : "decrease"} />
              {t('university.students.overall_completion')}
            </StatHelpText>
          </Stat>
        </SimpleGrid>
      </Panel>

      {/* Search and Filters */}
      <Panel heading={t('university.students.search_students')} padding={6}>
        <InputGroup>
          <InputLeftElement pointerEvents="none">
            <Icon as={MdSearch} color="gray.300" />
          </InputLeftElement>
          <Input
            placeholder={t('university.students.search_by_student_name_email_course')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </InputGroup>
      </Panel>

      {/* Students List */}
      <Panel heading={t('university.students.all_students')} padding={6}>
        {students.length === 0 ? (
          <AnimatedCard bg={cardBg} p={6}>
            <Center>
              <VStack spacing={3}>
                <Icon as={MdSchool} boxSize={8} color="gray.400" />
                <Text color="gray.500">{t('university.students.no_students_found')}</Text>
                <Text fontSize="sm" color="gray.400">
                  {searchTerm ? t('university.students.no_students_match_search') : t('university.students.students_will_appear_here')}
                </Text>
              </VStack>
            </Center>
          </AnimatedCard>
        ) : (
          <MotionBox
            initial="hidden"
            animate="visible"
            variants={containerVariants}
            as={VStack}
            align="stretch"
            spacing={4}
          >
            {filteredStudents.map((student) => (
              <AnimatedCard key={student.id} bg={cardBg} p={4} variants={cardVariants}>
                <VStack align="stretch" spacing={4}>
                  {/* Student Header */}
                  <HStack justify="space-between" align="start">
                    <HStack spacing={4}>
                      <Avatar
                        size="md"
                        name={student.user?.name || 'Student'}
                        src={student.user?.email ? `https://ui-avatars.com/api/?name=${encodeURIComponent(student.user.name)}&background=random` : undefined}
                      />
                      <VStack align="start" spacing={1}>
                        <Text fontWeight="semibold" fontSize="lg">
                          {student.user?.name || 'Anonymous Student'}
                        </Text>
                        <Text fontSize="sm" color="gray.500">
                          {student.user?.email || 'No email provided'}
                        </Text>
                        <HStack spacing={2}>
                          {student.course && (
                            <Badge colorScheme="blue">
                              {student.course.title}
                            </Badge>
                          )}
                          {student.grade && (
                            <Badge colorScheme="purple">
                              {t('university.students.grade')}: {student.grade}%
                            </Badge>
                          )}
                        </HStack>
                      </VStack>
                    </HStack>

                    <HStack spacing={2}>
                      <Tooltip label={t('university.students.view_details')}>
                        <IconButton
                          size="sm"
                          variant="ghost"
                          icon={<Icon as={MdVisibility} />}
                          aria-label={t('university.students.view_student_details')}
                        />
                      </Tooltip>
                      <Tooltip label={t('university.students.edit_student')}>
                        <IconButton
                          size="sm"
                          variant="ghost"
                          icon={<Icon as={MdEdit} />}
                          aria-label={t('university.students.edit_student')}
                        />
                      </Tooltip>
                      <Tooltip label={t('university.students.unenroll_student')}>
                        <IconButton
                          size="sm"
                          variant="ghost"
                          colorScheme="red"
                          icon={<Icon as={MdDelete} />}
                          onClick={() => {
                            setSelectedStudent(student);
                            onUnenrollModalOpen();
                          }}
                          aria-label={t('university.students.unenroll_student')}
                        />
                      </Tooltip>
                    </HStack>
                  </HStack>

                  <Divider />

                  {/* Progress Information */}
                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                    <Box>
                      <Text fontSize="sm" fontWeight="semibold" mb={2}>{t('university.students.overall_progress')}</Text>
                      <Progress
                        value={student.overallProgress}
                        colorScheme={getProgressColor(student.overallProgress)}
                        size="lg"
                        borderRadius="full"
                        mb={2}
                      />
                      <Text fontSize="sm" color="gray.600">
                        {t('university.students.complete_percentage', { percentage: student.overallProgress })}
                      </Text>
                    </Box>

                    <Box>
                      <Text fontSize="sm" fontWeight="semibold" mb={2}>{t('university.students.modules_progress')}</Text>
                      <Text fontSize="sm" color="gray.600">
                        {student.completedModules}/{student.totalModules} {t('university.students.modules_completed')}
                      </Text>
                      <Text fontSize="sm" color="gray.600">
                        {student.completedContents}/{student.totalContents} {t('university.students.contents_completed')}
                      </Text>
                    </Box>
                  </SimpleGrid>

                  {/* Additional Details */}
                  <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
                    <Box>
                      <HStack spacing={2} mb={1}>
                        <Icon as={MdCalendarToday} color="gray.500" />
                        <Text fontSize="xs" color="gray.500">{t('university.students.enrolled_date')}</Text>
                      </HStack>
                      <Text fontSize="sm">
                        {student.enrolledAt ? formatDate(student.enrolledAt) : t('university.students.unknown')}
                      </Text>
                    </Box>

                    <Box>
                      <HStack spacing={2} mb={1}>
                        <Icon as={MdAccessTime} color="gray.500" />
                        <Text fontSize="xs" color="gray.500">{t('university.students.last_access')}</Text>
                      </HStack>
                      <Text fontSize="sm">
                        {student.lastAccessedAt ? formatDate(student.lastAccessedAt) : t('university.students.never')}
                      </Text>
                    </Box>
                  </SimpleGrid>

                  {/* Notes */}
                  {student.notes && (
                    <Box>
                      <Text fontSize="sm" fontWeight="semibold" mb={1}>{t('university.students.notes')}</Text>
                      <Text fontSize="sm" color="gray.600" fontStyle="italic">
                        {student.notes}
                      </Text>
                    </Box>
                  )}
                </VStack>
              </AnimatedCard>
            ))}
          </MotionBox>
        )}
      </Panel>

      {/* Unenroll Confirmation Modal */}
      <Modal isOpen={isUnenrollModalOpen} onClose={onUnenrollModalClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{t('university.students.unenroll_student')}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Alert status="warning" mb={4}>
              <AlertIcon />
              <Box>
                <AlertTitle>{t('university.students.warning')}</AlertTitle>
                <AlertDescription>
                  {t('university.students.unenroll_warning_description')}
                </AlertDescription>
              </Box>
            </Alert>
            <Text>
              {t('university.students.confirm_unenroll', {
                studentName: selectedStudent?.user?.name || t('university.students.this_student'),
                courseTitle: selectedStudent?.course?.title || t('university.students.the_course')
              })}
            </Text>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onUnenrollModalClose}>
              {t('university.students.cancel')}
            </Button>
            <Button
              colorScheme="red"
              onClick={handleUnenrollStudent}
              isLoading={unenrolling}
              loadingText={t('university.students.unenrolling')}
            >
              {t('university.students.unenroll_student')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </VStack>
  );
};

export default StudentsListPage; 